import { Mobile } from '@bimser/common';
import {
	BCInput,
	BCInputMultiLanguage,
	BCInputRef,
	IInputChangeEventArgs,
	IInputProps
} from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper } from '@bimser/core';
import React, { JSX } from 'react';
import { SizeType } from '../../../../entities/common';
import MultiLanguageTextControl from '../../base/MultiLanguageTextControl';
import Styles from './assets/style.scss';
import { ITextBoxProperties } from './entities/ITextBoxProperties';

export default class TextBox extends MultiLanguageTextControl {
	sizeType: SizeType;
	inputRef = React.createRef<BCInputRef>();

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.TextBox;

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			this.triggerValueChanged({ data: value });
		});

		this.subscribe('trigger:OnLanguageValueChanged', (data: any) => {
			const mlData = JSON.parse(data);

			Object.entries(mlData).forEach(([culture, text]) =>
				this.__proxyChanging.multiLanguageText.setText(text as string, culture)
			);
		});
	}

	__loadTextProperty() {}

	__loadValueProperty() {}

	__loadProperties(data: ITextBoxProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.sizeType = data.sizeType;
	}

	protected triggerValueChanged(args: IInputChangeEventArgs) {
		if (Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable) {
			if (args.data?.length <= this.maxLength) {
				this.__proxyChanging.value = args.data;
			}
		} else {
			this.__proxyChanging.value = args.data;
		}
	}

	protected onLanguageValueChange(culture: string, text: string) {
		this.multiLanguageText.setText(text, culture);
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__renderMultiLanguageControl() {
		return (
			<BCInputMultiLanguage
				values={this.multiLanguageText.getData()}
				supportedLanguages={this.__form.supportedLanguages}
				currentLanguage={this.__form.currentLanguage}
				renderInput={this.__pureRender.bind(this)}
				onLanguageValueChange={this.onLanguageValueChange.bind(this)}
				size={this.__getSizeType()}
				placement="bottomRight"
				getPopupContainer={this.__form.__getShadowRootContainer}
				innerInputProps={{
					maxLength: this.maxLength,
					placeHolders: this.placeholder,
					readOnly: this.__isReadOnly(),
					disabled: this.__isDisabled(),
					showCount: this.showCharacterCounter
				}}
			/>
		);
	}

	focus(options?: { cursor?: 'start' | 'end' | 'all'; preventScroll?: boolean }) {
		this.inputRef.current?.focus(options);
	}

	__pureRender() {
		return (
			<BCInput
				ref={this.inputRef}
				onChange={this.triggerValueChanged.bind(this)}
				className={Styles.transparentContent}
				tabIndex={this.tabIndex}
				placeHolder={
					MLHelper.getMLText(this.multiLanguageText.getData()) ||
					this.placeholder.getText()
				}
				maxLength={this.maxLength}
				value={this.value || this.text}
				style={this.style}
				readOnly={this.__isReadOnly()}
				disabled={this.__isDisabled()}
				size={this.__getSizeType()}
				textAlign={this.textAlign}
				showCount={this.showCharacterCounter}
			/>
		);
	}

	__getComponentData(): IInputProps & IDictionary<any> {
		return {
			...super.__getComponentData(),
			value: this.multiLanguageText.getText(this.__form.currentLanguage.culture, true) || '',
			readOnly: this.__isReadOnly(),
			disabled: this.__isDisabled()
		};
	}

	__renderControl() {
		if (this.enableMultiLanguageText) {
			return this.__renderMultiLanguageControl();
		} else {
			return this.__pureRender();
		}
	}

	__render(children?: JSX.Element) {
		return super.__render(this.__renderControl());
	}
}
